// Peer-group-specialized factor reads (2026-05-14 expansion).
// Bank: rotce + tier1/cet1/tceRatio + npl/provCoverage/ncoRate/loanToDeposit
// REIT: ffoYield + occupancyRate + sameStoreNoiGrowth + reAvgLeaseTerm + pToFfo + pToNav
// Universal Safety: cdsSpread + shortInterestDaysToCover
// Innovation/Dilution: rdIntensity + sbcDilution
//
// Extracted from factor-pipeline.ts to keep that file under the 500-line
// god-file threshold and to give future per-peer-group additions a clean
// home. Gating (properBank / reitLike / bankLike) lives in factor-pipeline.ts
// where the StockData is available — this module is read-only computation
// from override + price + marketCap.

import type { StockData, StockOverride } from '../types';

function safe(n: number | undefined | null): number | null {
  if (n == null || !isFinite(n) || isNaN(n)) return null;
  return n;
}

export interface PeerGroupFactorReads {
  // Bank (financial flavor, gated to isProperBank)
  tier1CapRatio: number | null;
  cet1Ratio: number | null;
  tceRatio: number | null;
  nplRatio: number | null;
  provCoverage: number | null;
  ncoRate: number | null;
  rotce: number | null;
  loanToDeposit: number | null;
  // Universal Safety (all flavors)
  cdsSpread: number | null;
  shortInterestDaysToCover: number | null;
  // Innovation + dilution (universal, primarily standard-flavor signal)
  rdIntensity: number | null;
  sbcDilution: number | null;
  // REIT (reit flavor, gated to isREITLike)
  ffoYield: number | null;
  occupancyRate: number | null;
  sameStoreNoiGrowth: number | null;
  reAvgLeaseTerm: number | null;
  pToFfo: number | null;
  pToNav: number | null;
}

/**
 * Read per-peer-group factors from the StockOverride + StockData. Pure: no
 * side effects, no closures over enclosing scope. Gating to peer group
 * happens at the call site in factor-pipeline.ts return block.
 */
export function readPeerGroupFactors(d: StockData, override: StockOverride | undefined): PeerGroupFactorReads {
  // Bank-specific direct ratios pass through; derived ratios (NPL, prov
  // coverage, NCO, ROTCE, loan/deposit) are computed in refresh-resolved.ts
  // buildOverride() and arrive here pre-built. All null for non-banks
  // (the source BBG fields return null there).
  const tier1CapRatio = override?.tier1CapRatio != null ? safe(override.tier1CapRatio / 100) : null;
  const cet1Ratio     = override?.cet1Ratio    != null ? safe(override.cet1Ratio    / 100) : null;
  const tceRatio      = override?.tceRatioBank != null ? safe(override.tceRatioBank / 100) : null;
  const nplRatio      = override?.nplRatio     != null ? safe(override.nplRatio)           : null;
  const provCoverage  = override?.provCoverage != null ? safe(override.provCoverage)       : null;
  const ncoRate       = override?.ncoRate      != null ? safe(override.ncoRate)            : null;
  const rotce         = override?.rotce        != null ? safe(override.rotce)              : null;
  const loanToDeposit = override?.loanToDeposit != null ? safe(override.loanToDeposit)     : null;

  // Universal Safety:
  // cdsSpread: market-implied default-risk gauge from credit-default-swap
  //   quotes (BBG RSK_BB_IMPLIED_CDS_SPREAD in bps). Lower = safer, invert.
  // shortInterestDaysToCover: days-to-cover short interest. Higher = more
  //   crowded short positioning = late-stage risk signal. Invert.
  const cdsSpread                = override?.impliedCdsSpread   != null ? safe(override.impliedCdsSpread)   : null;
  const shortInterestDaysToCover = override?.shortInterestRatio != null ? safe(override.shortInterestRatio) : null;

  // Innovation + dilution:
  // rdIntensity (R&D / revenue): compounder thesis support.
  // sbcDilution (SBC / revenue from buildOverride, inverted in pillar): share dilution.
  const rdIntensity = override?.rdIntensity != null ? safe(override.rdIntensity) : null;
  const sbcDilution = override?.sbcDilution != null ? safe(override.sbcDilution) : null;

  // REIT-specific:
  // ffoYield = FFO/share / price (REIT cash-yield)
  // pToFfo = price / FFO per share (canonical REIT multiple, invert in pillar)
  // pToNav uses bestNav in FULL DOLLARS (BBG returns BEST_NAV raw); navPerShare
  // = bestNav / sharesOutFull, sharesOutFull = marketCap / price.
  const ffoYield = override?.ffoPerShareDiluted != null && d.price != null && d.price > 0
    ? safe(override.ffoPerShareDiluted / d.price) : null;
  const pToFfo = override?.ffoPerShareDiluted != null && d.price != null && override.ffoPerShareDiluted > 0
    ? safe(d.price / override.ffoPerShareDiluted) : null;
  const occupancyRate      = override?.occupancyRate      != null ? safe(override.occupancyRate      / 100) : null;
  const sameStoreNoiGrowth = override?.sameStoreNoiGrowth != null ? safe(override.sameStoreNoiGrowth / 100) : null;
  const reAvgLeaseTerm     = override?.reAvgLeaseTerm     != null ? safe(override.reAvgLeaseTerm)           : null;

  const sharesOutGuess = (d.marketCap && d.price && d.price > 0) ? (d.marketCap / d.price) : null;
  const navPerShare = override?.bestNav != null && sharesOutGuess != null && sharesOutGuess > 0
    ? override.bestNav / sharesOutGuess : null;
  const pToNav = navPerShare != null && navPerShare > 0 && d.price != null
    ? safe(d.price / navPerShare) : null;

  return {
    tier1CapRatio, cet1Ratio, tceRatio, nplRatio, provCoverage, ncoRate, rotce, loanToDeposit,
    cdsSpread, shortInterestDaysToCover,
    rdIntensity, sbcDilution,
    ffoYield, occupancyRate, sameStoreNoiGrowth, reAvgLeaseTerm, pToFfo, pToNav,
  };
}
